import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { classNames } from "@/lib/format";
import {
  getIntegrationDef,
  schnittstellenOverviewHref,
  type SchnittstellenIntegrationId,
} from "@/pages/stammdaten/schnittstellenIntegrations";

interface IntegrationPageLayoutProps {
  integrationId: SchnittstellenIntegrationId;
  configured?: boolean;
  statusDetail?: string;
  loading?: boolean;
  /** Use for panels that bring their own card sections */
  bare?: boolean;
  children: React.ReactNode;
}

export function IntegrationPageLayout({
  integrationId,
  configured,
  statusDetail,
  loading,
  bare,
  children,
}: IntegrationPageLayoutProps) {
  const def = getIntegrationDef(integrationId);
  const Icon = def.icon;

  return (
    <div className="space-y-6">
      <Link
        to={schnittstellenOverviewHref()}
        className="inline-flex items-center gap-1.5 text-sm text-label-secondary transition-colors hover:text-accent"
      >
        <ChevronLeft size={16} strokeWidth={2} aria-hidden />
        Zurück zum Überblick
      </Link>

      <header
        className={classNames(
          "relative overflow-hidden rounded-2xl border border-separator",
          "bg-gradient-to-br from-card via-card to-fill-quaternary",
        )}
      >
        <div
          className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-30 blur-2xl"
          style={{
            background: `radial-gradient(circle, var(--color-accent) 0%, transparent 70%)`,
          }}
          aria-hidden
        />

        <div className="relative flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
          <div className="flex min-w-0 items-start gap-4">
            <span
              className={classNames(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
                def.accentClass,
              )}
              aria-hidden
            >
              <Icon size={24} strokeWidth={1.75} className={def.iconClass} />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight text-label">{def.label}</h2>
              <p className="mt-1 text-sm leading-relaxed text-label-secondary">{def.description}</p>
              {statusDetail && !loading ? (
                <p className="mt-2 text-xs text-label-tertiary">{statusDetail}</p>
              ) : null}
            </div>
          </div>

          {!loading && configured !== undefined ? (
            <span
              className={classNames(
                "shrink-0 self-start rounded-full px-3 py-1 text-xs font-semibold",
                configured
                  ? "bg-status-green/15 text-status-green"
                  : "border border-separator bg-fill-tertiary text-label-secondary",
              )}
            >
              {configured ? "Aktiv" : "Offen"}
            </span>
          ) : loading ? (
            <span className="shrink-0 self-start rounded-full bg-fill-tertiary px-3 py-1 text-xs text-label-tertiary">
              …
            </span>
          ) : null}
        </div>
      </header>

      {bare ? (
        children
      ) : (
        <div className="rounded-2xl border border-separator bg-card p-5 sm:p-6">{children}</div>
      )}
    </div>
  );
}
